import { z } from 'zod';
import { SignalManualSourceSchema } from './enums.js';

// Conventions:
//   - Timestamps: ISO 8601 with `Z` (UTC). DB columns are timestamptz.
//   - Numerics: number on the wire (DB sends string; routes coerce).
//   - Idempotency: required on every write that can be retried.

// ─── signals.create — model side (X-Model-Token) ───────────────────────────

export const SignalCreateInputSchema = z.object({
  canonicalMatchId: z.string().uuid(),
  market: z.string().min(1).max(32),
  // 'p1' / 'p2' for h2h. 'over_22.5' / 'spread_p1_-2.5' for other markets.
  selection: z.string().min(1).max(64),
  // Decimal odds. Range matches storage-writer-tsdb's data quality guard.
  targetOdds: z.number().gte(1.01).lte(1000),
  // Optional 0..1; if omitted, scanner_config.min_confidence (if set) is bypassed.
  confidence: z.number().gte(0).lte(1).optional(),
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime(),
  modelId: z.string().min(1).max(64),
  modelVersion: z.string().min(1).max(64),
  // Free-form: features used, internal p_win, recommended_stake, etc.
  metadata: z.record(z.unknown()).default({}),
  // Idempotency: per-tenant unique. Two POSTs with same key return same row.
  idempotencyKey: z.string().min(8).max(128),
  // If set, the new signal supersedes an existing one in the same TX.
  supersedesId: z.string().uuid().optional(),
});
export type SignalCreateInput = z.infer<typeof SignalCreateInputSchema>;

// ─── signals.createManual — admin side (Supabase JWT) ──────────────────────
//
// Same `target_odds` semantics as SignalCreateInput, but:
//   - Server forces modelId='manual' + a per-operator modelVersion stamp
//   - validFrom = server now()
//   - validUntil derived from validUntilMinutes (UI doesn't pass ISO directly)
//   - Optional source label (recorded in metadata.source) for UI badges
//   - Optional manual stake (recorded in metadata.manualStakeUsd) — when the
//     model/operator already specified a stake, the dashboard surfaces it
//     alongside the Kelly recommendation so the operator can compare.
export const SignalCreateManualInputSchema = z.object({
  canonicalMatchId: z.string().uuid(),
  market: z.string().min(1).max(32),
  selection: z.string().min(1).max(64),
  targetOdds: z.number().gte(1.01).lte(1000),
  // Operator's preferred window. Server clamps the resulting validUntil to
  // be at least `match.starts_at + 30 min` for prematch picks, so a small
  // value here is safe — the floor protects against expiring before kickoff.
  // Max raised to 14 days to cover picks made well in advance of a tournament.
  validUntilMinutes: z
    .number()
    .int()
    .min(15)
    .max(60 * 24 * 14)
    .default(240),
  source: SignalManualSourceSchema.default('manual'),
  notes: z.string().max(500).optional(),
  // Optional dollar stake the operator/model wants to use. Stored in
  // signal.metadata.manualStakeUsd. UI shows both this and Kelly side-by-side.
  manualStakeUsd: z.number().nonnegative().max(1_000_000).optional(),
  idempotencyKey: z.string().uuid(),
});

// signals.supersedeManual — operator corrects a manual pick by emitting a
// new signal that supersedes the previous one. Same downstream effects as
// createManual + automatically marks the original signal as superseded in
// the same TX. UI surfaces this as the "Corregir" button on tracked picks.
//
// supersedesId is the ONLY field that distinguishes this from createManual
// at the schema level — but having a dedicated route makes the operator
// intent explicit in the audit log and keeps the input schema small.
export const SignalSupersedeManualInputSchema = z.object({
  supersedesId: z.string().uuid(),
  // The new pick's payload. Most fields default from the original on the
  // UI side; the operator typically only changes targetOdds and/or
  // validUntilMinutes when correcting.
  targetOdds: z.number().gte(1.01).lte(1000),
  validUntilMinutes: z
    .number()
    .int()
    .min(15)
    .max(60 * 24)
    .default(240),
  manualStakeUsd: z.number().nonnegative().max(1_000_000).optional(),
  notes: z.string().max(500).optional(),
  idempotencyKey: z.string().uuid(),
});
export type SignalSupersedeManualInput = z.infer<typeof SignalSupersedeManualInputSchema>;
export type SignalCreateManualInput = z.infer<typeof SignalCreateManualInputSchema>;

// ─── signals.list / signals.create response ────────────────────────────────

export const SignalRowSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  modelId: z.string(),
  modelVersion: z.string(),
  canonicalMatchId: z.string().uuid(),
  market: z.string(),
  selection: z.string(),
  targetOdds: z.number(),
  confidence: z.number().nullable(),
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime(),
  metadata: z.record(z.unknown()),
  supersededBy: z.string().uuid().nullable(),
  idempotencyKey: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type SignalRow = z.infer<typeof SignalRowSchema>;

export const SignalListFilterSchema = z.object({
  matchId: z.string().uuid().optional(),
  modelId: z.string().min(1).max(64).optional(),
  // false = active only (superseded_by IS NULL). true = include all history.
  includeSuperseded: z.boolean().default(false),
  limit: z.number().int().min(1).max(500).default(100),
});
export type SignalListFilter = z.infer<typeof SignalListFilterSchema>;

// signals.tracked — every active pick the scanner is watching, with the
// current best book/odds + edge across allowed_books from the Redis hot
// cache. Drives the "Tus picks vigilados" section on the dashboard so an
// operator always sees their pick (red until value, green when value hits).

export const TrackedSignalBookOddsSchema = z.object({
  book: z.string(),
  odds: z.number(),
  observedAt: z.string().datetime(),
  isLive: z.boolean(),
  edgePct: z.number(), // (odds - target) / target
  // True when this book is in scanner_config.allowed_books → it generates
  // alerts/opportunities. False = market reference only (operator does NOT
  // operate against it). UI dims/styles non-allowed differently.
  isAllowed: z.boolean(),
});
export type TrackedSignalBookOdds = z.infer<typeof TrackedSignalBookOddsSchema>;

// Match metadata embedded in every tracked signal so the dashboard card
// can render names + tournament + start time without a second round-trip
// to matches.search. All fields nullable because the connector may not
// have linked vendor IDs yet at the moment a signal is created.
export const TrackedSignalMatchSchema = z.object({
  matchId: z.string().uuid(),
  startsAt: z.string().datetime().nullable(),
  status: z.string().nullable(),
  surface: z.string().nullable(),
  bestOf: z.number().int().nullable(),
  p1Name: z.string().nullable(),
  p2Name: z.string().nullable(),
  tournamentName: z.string().nullable(),
  tournamentLevel: z.string().nullable(),
});
export type TrackedSignalMatch = z.infer<typeof TrackedSignalMatchSchema>;

export const TrackedSignalRowSchema = z.object({
  signal: SignalRowSchema,
  // Match metadata for rendering — see TrackedSignalMatchSchema.
  match: TrackedSignalMatchSchema,
  // Best book across allowed_books. null if no hot-cache hit for any book
  // (= scanner hasn't seen odds yet, or all books gone stale).
  bestBook: z.string().nullable(),
  bestOdds: z.number().nullable(),
  bestEdgePct: z.number().nullable(),
  bestObservedAt: z.string().datetime().nullable(),
  // Per-book breakdown for the inline grid in the UI.
  perBook: z.array(TrackedSignalBookOddsSchema),
  // Linked opportunity if one exists. UI can navigate / show actions.
  opportunityId: z.string().uuid().nullable(),
  opportunityState: z.string().nullable(),
});
export type TrackedSignalRow = z.infer<typeof TrackedSignalRowSchema>;

export const SignalsTrackedFilterSchema = z.object({
  // false = scoped to active+non-superseded+within-validity. true = include
  // expired or already-executed (read-only forensic view).
  includeInactive: z.boolean().default(false),
  limit: z.number().int().min(1).max(200).default(100),
});
export type SignalsTrackedFilter = z.infer<typeof SignalsTrackedFilterSchema>;
